// The extracted ItemNavigationControls component
import { IconButton, setRef, Stack, styled, Tooltip } from "@mui/material";
import { MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import { propEq } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";
import { RateBuilderDragHandle } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RowBody/ItemMoveControls/RateBuilderDragHandle";

interface ItemMoveControlsProps {
  isWrapped: boolean;
  item: {
    item: { id: string };
    parent?: { id: string | null; children?: { id: string }[] } | null;
  };
  dragHandlerRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Renders moving controls for a tree item, including
 */
export const ItemMoveControls: React.FC<ItemMoveControlsProps> = ({ isWrapped, item, dragHandlerRef }) => {
  const { t } = useTranslation();
  const { move } = useRateTreeContext();
  const itemId = item.item.id;
  const itemParent = item.parent;
  const parentId = itemParent?.id ?? null;
  const itemIndex = itemParent?.children?.findIndex(propEq("id", itemId));
  const totalItems = itemParent?.children?.length ?? 0;

  const isDraggable = !isWrapped || itemIndex === 0;

  const handleMoveUp = useCallback(() => {
    move(itemId, parentId, (itemIndex ?? 0) - 1);
  }, [move, itemId, parentId, itemIndex]);

  const handleMoveDown = useCallback(() => {
    move(itemId, parentId, (itemIndex ?? 0) + 1);
  }, [move, itemId, parentId, itemIndex]);

  return (
    <DragControlsStack
      ref={(el) => {
        setRef(dragHandlerRef, el);
      }}
      alignItems="center"
      isDraggable={isDraggable}
      justifyContent="space-between"
    >
      {/* Up button */}
      <DragControlContainer>
        {isWrapped && isNotNil(itemIndex) && itemIndex > 0 && (
          <DenseIconButton onClick={handleMoveUp}>
            <MdsIcon fontSize="xs" icon={MdsKeyboardArrowUp} />
          </DenseIconButton>
        )}
      </DragControlContainer>

      {/* Drag handle */}
      <Tooltip disableFocusListener title={t("RateBuilder.dragToMove")}>
        <DragControlContainer>{isDraggable && <RateBuilderDragHandle />}</DragControlContainer>
      </Tooltip>

      {/* Down button */}
      <DragControlContainer>
        {isWrapped && isNotNil(itemIndex) && totalItems - 1 !== itemIndex && (
          <DenseIconButton onClick={handleMoveDown}>
            <MdsIcon fontSize="xs" icon={MdsKeyboardArrowDown} />
          </DenseIconButton>
        )}
      </DragControlContainer>
    </DragControlsStack>
  );
};

const DragControlContainer = styled("div")(() => ({ height: "16px", display: "inherit" }));

const DenseIconButton = styled(IconButton)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
}));

const DragControlsStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isDraggable",
})<{ isDraggable: boolean }>(({ theme, isDraggable }) => ({
  marginTop: theme.spacing(-0.5),
  marginBottom: theme.spacing(-0.5),
  cursor: isDraggable ? "grab !important" : "inherit",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  transform: `translate(${theme.spacing(-1)}, 0px)`,
  marginRight: theme.spacing(-2),
  minHeight: "100%",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    outline: `1px solid ${theme.palette.action.focus}`,
    outlineOffset: "-1px",
    borderTopLeftRadius: `${theme.shape.borderRadius}px !important`,
    borderBottomLeftRadius: `${theme.shape.borderRadius}px !important`,
  },
}));
